import { Bell, BellRing, CalendarCheck, CircleCheck, CircleX, Hash, Mail, MousePointerClick, Plus, Send, Smartphone, Trash2, Webhook } from '@orgus/ui/icons';
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { NOTIFY_TRIGGERS } from '@orgus/protocol';
import type { ChannelView, NotifyMember, NotifySettings, NotifyTrigger, NotifyView, Urgency, WebhookFormat } from '@orgus/protocol';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Panel,
  RelativeTime,
  Segmented,
  Select,
  SkeletonRows,
  toast,
  toUiError,
} from '@orgus/ui';
import type { Tone, UiError } from '@orgus/ui';
import { useOrgus } from '../../../lib/store.ts';
import { PhoneAccessHelp } from '../../mobile/InstallCard.tsx';
import { addWebhook, deleteChannel, getNotify, patchChannel, saveNotifySettings, testSend, useNotifyState } from '../../notify/api.ts';
import { AddEmailChannel, ProviderEmail, SmtpForm } from '../../notify/EmailSettings.tsx';
import { WhenRuns } from '../../server/ModuleAbsent.tsx';
import { kindsSummary } from '../../notify/kinds.ts';
import { PushControl } from '../../notify/PushControl.tsx';
import { NoChannelNudge } from '../../notify/NoChannelNudge.tsx';
import { SlackButtonsDialog } from '../../notify/SlackButtonsDialog.tsx';
import { SummaryPreview } from '../../notify/SummaryPreview.tsx';
import type { SettingsSection } from '../registry.ts';

const URGENCY_OPTIONS: { value: Urgency; label: string }[] = [
  { value: 'low', label: 'All, low in the digest' },
  { value: 'normal', label: 'Normal and above' },
  { value: 'high', label: 'High and critical' },
  { value: 'critical', label: 'Critical only' },
];

const URGENCY_TONE: Record<Urgency, Tone> = { critical: 'failed', high: 'waiting', normal: 'neutral', low: 'neutral' };
const URGENCY_LABEL: Record<Urgency, string> = { critical: 'Critical', high: 'High', normal: 'Normal', low: 'Low' };

const TRIGGER_LABEL: Record<NotifyTrigger, { title: string; detail: string }> = {
  'approval.pending': { title: 'Approval or question waiting', detail: 'High. With Approve and Deny where the device shows buttons.' },
  'session.waiting': { title: 'A session is waiting for input', detail: 'High. At most once every 2 minutes per session.' },
  'machine.offline': { title: 'A machine went offline', detail: 'High, after a minute offline. Low when it is back.' },
  'session.unmetered': { title: 'A session is not metered', detail: 'High. No usage arrived 5 minutes after it started, so no budget counts it.' },
  'budget.alert': { title: 'A budget nears or hits its limit', detail: 'High near the limit, critical at it.' },
  'pipeline.failed': { title: 'A pipeline item failed', detail: 'Normal.' },
  'session.ended': { title: 'A session finished', detail: 'Low: rolls into the daily digest.' },
  'mission.landed': { title: 'A mission landed', detail: 'Low. Names the item and the commit.' },
  'item.done': { title: 'An item is done', detail: 'Low: rolls into the daily digest.' },
  'deploy.done': { title: 'A deploy finished', detail: 'Low. Names the target and the version.' },
  'deploy.rolled-back': { title: 'A deploy rolled back', detail: 'High. Names the target and the version.' },
};

const FORMAT_LABEL: Record<WebhookFormat, string> = { slack: 'Slack', teams: 'Teams', json: 'JSON' };

function ChannelRow({ c, members, publicUrl, onChanged }: { c: ChannelView; members: NotifyMember[]; publicUrl: string | null; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [slackSetup, setSlackSetup] = useState(false);
  /** Why the last change to this channel failed: shown on the row, never in a toast. */
  const [error, setError] = useState<UiError | null>(null);
  const owner = members.find((m) => m.id === c.memberId);
  const canOneTap = c.kind === 'email' || (c.kind === 'webhook' && !c.interactive);
  const act = async (what: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(what);
    setError(null);
    try {
      await fn();
      if (ok) toast.success(ok, c.name);
      onChanged();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  };
  const Icon = c.kind === 'push' ? Smartphone : c.kind === 'email' ? Mail : c.format === 'slack' ? Hash : Webhook;
  return (
    <li className="flex flex-col gap-3 px-4 py-3">
      <div className="flex items-start gap-3">
        <Icon className="mt-1 size-4 shrink-0 text-fg-subtle" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ui font-medium text-fg">
            <span className="truncate">{c.name}</span>
            {c.kind === 'webhook' && c.format && <Badge>{FORMAT_LABEL[c.format]}</Badge>}
            {c.kind === 'email' && <Badge>Email</Badge>}
            {owner ? <Badge variant="outline">{owner.name}</Badge> : c.kind !== 'push' ? <Badge variant="outline">Shared</Badge> : null}
            {c.interactive && (
              <Badge tone="passed" icon={<MousePointerClick />}>
                Buttons answer in Slack
              </Badge>
            )}
            {c.oneTap && !c.interactive && (
              <Badge tone="accent" icon={<MousePointerClick />}>
                One-tap links
              </Badge>
            )}
            {!c.enabled && <Badge tone="idle">Paused</Badge>}
          </p>
          <p className="truncate text-caption text-fg-subtle">
            {c.kind === 'push' ? 'Push to this browser or device' : c.kind === 'email' ? c.address : c.target}
            {c.quietHours ? `, quiet ${c.quietHours.from} to ${c.quietHours.to}` : ''}
          </p>
          {c.kinds && <p className="truncate text-caption text-fg-subtle">Gets {kindsSummary(c.kinds).toLowerCase()}</p>}
          {c.last && (
            <p className={c.last.ok ? 'flex items-center gap-1 text-caption text-fg-subtle' : 'flex items-center gap-1 text-caption text-failed'}>
              {c.last.ok ? <CircleCheck className="size-3.5 text-passed" aria-hidden /> : <CircleX className="size-3.5" aria-hidden />}
              {c.last.ok ? 'Delivered' : `Failed: ${c.last.message ?? 'no reason given'}`} <RelativeTime ts={c.last.at} />
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" variant="ghost" icon={<Send />} loading={busy === 'test'} onClick={() => void act('test', () => testSend(c.id), 'Test sent')}>
            Test
          </Button>
          <Button size="sm" variant="ghost" loading={busy === 'pause'} onClick={() => void act('pause', () => patchChannel(c.id, { enabled: !c.enabled }))}>
            {c.enabled ? 'Pause' : 'Resume'}
          </Button>
          <IconButton label={`Remove ${c.name}`} icon={<Trash2 />} onClick={() => setRemoving(true)} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pl-8">
        <Select
          size="sm"
          aria-label={`Lowest urgency ${c.name} receives`}
          value={c.minUrgency}
          options={URGENCY_OPTIONS}
          wrapperClassName="w-52"
          onValueChange={(v) => void act('urgency', () => patchChannel(c.id, { minUrgency: v as Urgency }))}
        />
        {members.length > 0 && (
          <Select
            size="sm"
            aria-label={`Whose channel ${c.name} is`}
            value={c.memberId ?? ''}
            options={[{ value: '', label: 'Shared with the team' }, ...members.map((m) => ({ value: m.id, label: `${m.name} only` }))]}
            wrapperClassName="w-48"
            onValueChange={(v) => void act('owner', () => patchChannel(c.id, { memberId: v || null }))}
          />
        )}
        {c.kind === 'webhook' && c.format === 'slack' && (
          <Button size="sm" variant="outline" icon={<MousePointerClick />} onClick={() => setSlackSetup(true)}>
            {c.interactive ? 'Slack buttons' : 'Answer from Slack'}
          </Button>
        )}
        {canOneTap && (
          <Button
            size="sm"
            variant="outline"
            loading={busy === 'onetap'}
            onClick={() => void act('onetap', () => patchChannel(c.id, { oneTap: !c.oneTap }), c.oneTap ? 'Links only open Orgus now' : 'One-tap links on')}
          >
            {c.oneTap ? 'Turn off one-tap links' : 'Turn on one-tap links'}
          </Button>
        )}
      </div>
      {error && (
        <div className="pl-8">
          <ErrorState variant="inline" error={error} />
        </div>
      )}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title={`Remove "${c.name}"?`}
        description={
          c.kind === 'push'
            ? 'This device stops getting notifications until it turns them on again.'
            : c.kind === 'email'
              ? 'Orgus stops emailing this address.'
              : 'Orgus stops posting to this webhook. The URL is forgotten.'
        }
        confirmLabel="Remove channel"
        onConfirm={async () => {
          await deleteChannel(c.id);
          onChanged();
        }}
      />
      {c.kind === 'webhook' && c.format === 'slack' && <SlackButtonsDialog open={slackSetup} onOpenChange={setSlackSetup} channel={c} publicUrl={publicUrl} onSaved={onChanged} />}
    </li>
  );
}

function AddWebhook({ onAdded }: { onAdded: () => void }) {
  const [format, setFormat] = useState<WebhookFormat>('slack');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [minUrgency, setMin] = useState<Urgency>('high');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await addWebhook({ name: name.trim() || `${FORMAT_LABEL[format]} webhook`, format, url: url.trim(), minUrgency });
      toast.success('Webhook added', 'Send a test to check it.');
      setName('');
      setUrl('');
      onAdded();
    } catch (err) {
      const u = toUiError(err);
      setError(`${u.why} ${u.next}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div>
        <Segmented<WebhookFormat>
          label="Webhook format"
          value={format}
          onChange={setFormat}
          options={[
            { value: 'slack', label: 'Slack' },
            { value: 'teams', label: 'Teams' },
            { value: 'json', label: 'JSON' },
          ]}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Name" placeholder={format === 'slack' ? 'Slack #ops' : format === 'teams' ? 'Teams Engineering' : 'Pager bridge'} value={name} onChange={(e) => setName(e.target.value)} />
        <Select label="Sends" value={minUrgency} options={URGENCY_OPTIONS} onValueChange={(v) => setMin(v as Urgency)} />
      </div>
      <Input
        label="Webhook URL"
        type="url"
        required
        autoComplete="off"
        spellCheck={false}
        placeholder={format === 'slack' ? 'https://hooks.slack.com/services/...' : format === 'teams' ? 'https://...webhook.office.com/... or a Workflows URL' : 'https://...'}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        error={error ?? undefined}
        hint={
          error
            ? undefined
            : format === 'slack'
              ? 'Create it in Slack: Apps, Incoming Webhooks, Add to channel. The URL is stored on the hub and never shown again.'
              : format === 'teams'
                ? 'Create it in Teams with the Workflows app (Post to a channel when a webhook request is received). Stored on the hub, never shown again.'
                : 'Orgus posts the notification as JSON. Stored on the hub, never shown again.'
        }
      />
      <div>
        <Button type="submit" variant="secondary" icon={<Plus />} loading={busy} disabled={!url.trim()}>
          Add webhook
        </Button>
      </div>
    </form>
  );
}

function Rules({ view, onSaved }: { view: NotifyView; onSaved: (v: NotifyView) => void }) {
  const [s, setS] = useState<NotifySettings>(view.settings);
  const [busy, setBusy] = useState(false);
  /** Why saving failed: shown beside Save rules, never in a toast. */
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => setS(view.settings), [view.settings]);
  const dirty = JSON.stringify(s) !== JSON.stringify(view.settings);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await saveNotifySettings({ ...s, publicUrl: s.publicUrl?.trim() || null }));
      toast.success('Notification rules saved');
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <p className="text-caption font-medium text-fg-muted">Quiet hours</p>
          <Segmented<'off' | 'on'>
            label="Quiet hours"
            className="self-start"
            value={s.quietHours ? 'on' : 'off'}
            onChange={(v) => setS({ ...s, quietHours: v === 'on' ? (s.quietHours ?? { from: '22:00', to: '07:00' }) : null })}
            options={[
              { value: 'off', label: 'Off' },
              { value: 'on', label: 'On' },
            ]}
          />
          {s.quietHours && (
            <div className="flex items-end gap-2">
              <Input label="From" type="time" value={s.quietHours.from} onChange={(e) => setS({ ...s, quietHours: { ...s.quietHours!, from: e.target.value } })} />
              <Input label="To" type="time" value={s.quietHours.to} onChange={(e) => setS({ ...s, quietHours: { ...s.quietHours!, to: e.target.value } })} />
            </div>
          )}
          <p className="text-caption text-fg-subtle">Only critical gets through in quiet hours. The rest waits for the digest. Times are the hub machine's.</p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-caption font-medium text-fg-muted">Daily digest</p>
          <Segmented<'off' | 'on'>
            label="Daily digest"
            className="self-start"
            value={s.digestAt ? 'on' : 'off'}
            onChange={(v) => setS({ ...s, digestAt: v === 'on' ? (s.digestAt ?? '18:00') : null })}
            options={[
              { value: 'off', label: 'Off' },
              { value: 'on', label: 'On' },
            ]}
          />
          {s.digestAt && <Input label="Send at" type="time" value={s.digestAt} onChange={(e) => setS({ ...s, digestAt: e.target.value })} />}
          <p className="text-caption text-fg-subtle">Sessions, failures, approvals and spend for the day, plus everything held back. {view.digestQueued} waiting now.</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-caption font-medium text-fg-muted">Low-urgency events</p>
        <Segmented<string>
          label="Low-urgency events"
          className="self-start"
          value={s.batchMinutes ? String(s.batchMinutes) : 'summary'}
          onChange={(v) => setS({ ...s, batchMinutes: v === 'summary' ? null : Number(v) })}
          options={[
            { value: 'summary', label: 'In the daily summary' },
            { value: '15', label: 'Every 15 min' },
            { value: '60', label: 'Hourly' },
            { value: '240', label: 'Every 4 h' },
          ]}
        />
        <p className="text-caption text-fg-subtle">Finished sessions and other low-urgency events roll into one message per channel, outside quiet hours.</p>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-caption font-medium text-fg-muted">What raises a notification</p>
        <ul className="divide-y divide-border-subtle rounded-control border border-border">
          {NOTIFY_TRIGGERS.map((t) => (
            <li key={t} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-ui text-fg">{TRIGGER_LABEL[t].title}</p>
                <p className="text-caption text-fg-subtle">{TRIGGER_LABEL[t].detail}</p>
              </div>
              <Segmented<'on' | 'off'>
                label={TRIGGER_LABEL[t].title}
                value={s.triggers[t] ? 'on' : 'off'}
                onChange={(v) => setS({ ...s, triggers: { ...s.triggers, [t]: v === 'on' } })}
                options={[
                  { value: 'on', label: 'On' },
                  { value: 'off', label: 'Off' },
                ]}
              />
            </li>
          ))}
        </ul>
      </div>

      <Input
        label="Public address for links"
        placeholder="https://studio.your-tailnet.ts.net"
        value={s.publicUrl ?? ''}
        onChange={(e) => setS({ ...s, publicUrl: e.target.value })}
        hint="Slack, Teams and email link here, and one-tap links and Slack buttons need it. Leave empty and messages carry no link."
      />
      {error && <ErrorState variant="inline" error={error} onRetry={save} />}
      <div>
        <Button variant="primary" loading={busy} disabled={!dirty} onClick={() => void save()}>
          Save rules
        </Button>
      </div>
    </div>
  );
}

function Notifications() {
  const live = useNotifyState();
  const mode = useOrgus((s) => s.mode);
  const [view, setView] = useState<NotifyView | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setView(await getNotify());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'mock') {
      setLoading(false);
      return;
    }
    void load();
  }, [load, mode]);

  if (mode === 'mock') {
    return <EmptyState icon={<Bell />} title="Notifications need a hub" description="Demo data sends nothing. Connect to a hub to set up push, email, Slack and Teams." />;
  }
  if (loading && !view) return <SkeletonRows rows={6} />;
  if (error && !view) {
    const status = (error as { status?: number }).status;
    if (status === 404) {
      return <EmptyState icon={<Bell />} title="Notifications are not running on this hub" description="Update the hub to a version with the notify module, then reload this page." />;
    }
    return <ErrorState error={error} onRetry={load} />;
  }
  if (!view) return null;

  // Channels and the recent list update live; rules come from the last load or save.
  const channels = live?.channels ?? view.channels;
  const recent = live?.recent ?? view.recent;
  const members = view.members ?? [];

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <NoChannelNudge channels={channels} />
      <Panel title="This device" icon={<BellRing />}>
        <PushControl />
      </Panel>

      <Panel title="Channels" sub={channels.length} flush>
        {channels.length ? (
          <ul className="divide-y divide-border-subtle">
            {channels.map((c) => (
              <ChannelRow key={c.id} c={c} members={members} publicUrl={view.settings.publicUrl} onChanged={() => void load()} />
            ))}
          </ul>
        ) : (
          <EmptyState icon={<Bell />} title="No channels yet" description="Turn on push on this device, or add an email address, a Slack or a Teams webhook below. Every notification still shows in Recent." />
        )}
      </Panel>

      <Panel title="Add a Slack or Teams webhook" icon={<Webhook />}>
        <AddWebhook onAdded={() => void load()} />
      </Panel>

      <Panel title="Email" icon={<Mail />} sub={view.smtp?.configured ? undefined : 'Not set up'}>
        <WhenRuns module="/api/notify/smtp" feature="Email alerts" plural instead="Push, Slack and Teams alerts work today." size="compact">
        <div className="flex flex-col gap-4">
          {view.smtp && (view.smtp.provider ? <ProviderEmail smtp={view.smtp} onSent={() => void load()} /> : <SmtpForm smtp={view.smtp} onSaved={() => void load()} />)}
          <div className="flex flex-col gap-2 border-t border-border-subtle pt-4">
            <p className="text-caption font-medium text-fg-muted">Email addresses</p>
            <p className="text-caption text-fg-subtle">Email gets budget alerts, offline machines, unmetered sessions, escalations and the daily summary, not every session event.</p>
            <AddEmailChannel members={members} disabled={!view.smtp?.configured} onAdded={() => void load()} />
          </div>
        </div>
        </WhenRuns>
      </Panel>

      <Panel title="Rules">
        <Rules view={view} onSaved={setView} />
      </Panel>

      <Panel title="Daily summary" icon={<CalendarCheck />}>
        <SummaryPreview digestAt={view.settings.digestAt} />
      </Panel>

      <Panel title="Recent" sub={recent.length} flush>
        {recent.length ? (
          <ul className="divide-y divide-border-subtle">
            {recent.slice(0, 20).map((n) => (
              <li key={n.id} className="flex items-start gap-3 px-4 py-3">
                <Badge tone={URGENCY_TONE[n.urgency]}>{URGENCY_LABEL[n.urgency]}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-ui text-fg">{n.title}</p>
                  <p className="truncate text-caption text-fg-subtle">{n.body}</p>
                </div>
                <RelativeTime ts={n.at} className="shrink-0 text-caption text-fg-subtle" />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon={<Bell />} title="Nothing sent yet" description="Approvals, waiting sessions, offline machines, failures and budget alerts will show here, even with push off." />
        )}
      </Panel>

      <Panel title="Phone access">
        <PhoneAccessHelp />
      </Panel>
    </div>
  );
}

const section: SettingsSection = {
  id: 'notifications',
  title: 'Notifications',
  description: 'Push, email, Slack and Teams, quiet hours, batching and the daily summary.',
  order: 10,
  group: 'alerts',
  keywords: ['push', 'email', 'slack', 'teams', 'quiet hours', 'digest', 'webhooks'],
  icon: Bell,
  Component: Notifications,
};
export default section;
